import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { PLACEHOLDER_CHIPS, previewMessage, insertToken, reviewButtonsFor } from '../utils/orderMessagePreview';

// «Настройки → Статусы»: тексты уведомлений покупателю в боте под клиента.
// Подписи самих статусов НЕ настраиваются (решение юзера 23.09.2026 — «это нам точно не
// нужно»): они живут в коде, admin/src/orderStatus.js и src/orderStatusLabels.js.
// Набор текстов один на магазин. Самовывоз отличается путём заказа: письма «У курьера» у него
// нет (шаг помечен «только доставка»). «Новый» и «На упаковке» — внутренние, писем по ним нет.
// Ссылки на отзывы уходят инлайн-кнопками под письмом «Завершён», в текст их вставлять не надо.
// Сервер: GET/PUT /settings/order-statuses (backend/app/order_statuses.py).
const HINT = { color: '#999', fontSize: 12, whiteSpace: 'nowrap' };

function OrderStatusesSettings({ showToast }) {
  const [cfg, setCfg] = useState(null);
  const [messages, setMessages] = useState({});
  const [openEvent, setOpenEvent] = useState(null);   // какой текст письма раскрыт
  const [saving, setSaving] = useState(false);
  const areaRef = useRef(null);                        // раскрытая textarea — для вставки по курсору

  useEffect(() => {
    api.getOrderStatusesConfig()
      .then((c) => {
        setCfg(c);
        setMessages({ ...c.messages });
      })
      .catch(() => showToast('Не удалось загрузить настройки уведомлений', 'error'));
  }, [showToast]);

  if (!cfg) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  const deliveryOnly = cfg.delivery_only || [];
  const brandName = cfg.brand || '';
  const reviewLinks = cfg.review_links || {};
  const hasReviewLinks = Object.values(reviewLinks).some(Boolean);
  // Пусто, если совпадает с дефолтом — placeholder покажет дефолт
  const fieldValue = (ev) => (messages[ev] === cfg.defaults.messages[ev] ? '' : (messages[ev] || ''));

  // Вставка подстановки: по курсору, если сотрудник кликнул в текст, иначе в конец
  const insertPlaceholder = (ev, token) => {
    const el = areaRef.current;
    const focused = el && typeof document !== 'undefined' && document.activeElement === el;
    const caret = focused && typeof el.selectionStart === 'number' ? el.selectionStart : null;
    const res = insertToken(messages[ev] || '', token, caret);
    setMessages((prev) => ({ ...prev, [ev]: res.value }));
    if (el) {
      requestAnimationFrame(() => { el.focus(); el.setSelectionRange(res.caret, res.caret); });
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.updateOrderStatusesConfig({ messages });
      setMessages({ ...res.messages });
      setCfg((c) => ({ ...c, messages: res.messages }));
      showToast('Тексты уведомлений сохранены');
    } catch (e) {
      showToast(e.message || 'Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  const dirty = cfg.events.some((ev) => (messages[ev] || '') !== (cfg.messages[ev] || ''));
  const areDefault = cfg.events.every((ev) => (messages[ev] || '') === cfg.defaults.messages[ev]);

  return (
    <div className="card">
      <div className="card-title">💬 Уведомления покупателю в боте</div>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 8 }}>
        Текст сообщения, которое покупатель получает в Telegram или Max при смене статуса.
        Обычный текст без разметки: в Telegram первая строка выделяется жирным. Пустое поле —
        стандартный текст. О «Новом» заказе и о передаче на упаковку покупателю не пишем:
        первое письмо после оплаты — когда заказ собран. При самовывозе письма «У курьера» нет.
      </p>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>
        Пишите обычный текст. Если нужно подставить номер заказа или название магазина —
        нажмите кнопку под полем: она вставит подстановку там, где стоит курсор, а если в поле
        не вставали — допишет в конец. Ниже поля видно, что именно получит покупатель.
        Название магазина можно и просто написать словами: «{brandName}». Ссылки на отзывы
        в текст не вставляем — они уходят кнопками под письмом «Завершён».
      </p>
      {/* minmax(0, 1fr): превью текста в одну строку не раздувает колонку (страница была 843 px) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 6, maxWidth: 760 }}>
        {cfg.events.map((ev) => {
          const isOpen = openEvent === ev;
          const custom = (messages[ev] || '') !== cfg.defaults.messages[ev];
          const preview = (messages[ev] || cfg.defaults.messages[ev] || '').replace(/\n+/g, ' ').slice(0, 70);
          return (
            <div key={ev} style={{ border: '1px solid #eee', '--ch': '6px', '--stroke': '#eee'}}>
              <button
                onClick={() => setOpenEvent(isOpen ? null : ev)}
                style={{
                  width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                  padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <span style={{ width: 14, color: '#999', flexShrink: 0 }}>{isOpen ? '▾' : '▸'}</span>
                <span style={{ width: 150, flexShrink: 0, fontWeight: 600, fontSize: 14 }}>
                  {(cfg.event_titles[ev] || ev).split('(')[0].trim()}
                </span>
                <span style={{ flex: 1, minWidth: 0, color: '#999', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {preview}{preview.length >= 70 ? '…' : ''}
                </span>
                <span style={{ ...HINT, flexShrink: 0 }}>
                  {deliveryOnly.includes(ev) ? 'только доставка' : (custom ? 'изменён' : '')}
                </span>
              </button>
              {isOpen && (
                <div style={{ padding: '0 12px 12px 36px' }}>
                  <textarea
                    ref={areaRef}
                    className="form-input"
                    rows={ev === 'review_invite' ? 7 : 4}
                    maxLength={cfg.limits.message}
                    placeholder={cfg.defaults.messages[ev]}
                    value={fieldValue(ev)}
                    onChange={(e) => setMessages((prev) => ({ ...prev, [ev]: e.target.value }))}
                    onBlur={(e) => { if (!e.target.value.trim()) setMessages((prev) => ({ ...prev, [ev]: cfg.defaults.messages[ev] })); }}
                    style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4, width: '100%', boxSizing: 'border-box' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    <span style={{ color: '#999', fontSize: 12, marginRight: 2 }}>Вставить:</span>
                    {/* Вставка подстановки — ссылка-действие .btn-link (UX-аудит 25.10) */}
                    {PLACEHOLDER_CHIPS
                      .filter((chip) => !chip.only || chip.only === ev)
                      .map((chip) => (
                        <button
                          key={chip.insert}
                          type="button"
                          className="btn-link"
                          onClick={() => insertPlaceholder(ev, chip.insert)}
                          title={`Вставит ${chip.insert}`}
                          style={{ marginRight: 6 }}
                        >
                          + {chip.title}
                        </button>
                      ))}
                    <span style={{ flex: 1 }} />
                    <small style={{ color: '#999', fontSize: 12 }}>
                      {(messages[ev] || '').length} / {cfg.limits.message}
                    </small>
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>
                      Покупатель получит{ev === 'review_invite' ? ' (внутри письма «Завершён»)' : ''}:
                    </div>
                    <div style={{
                      background: '#F4F4EF', '--ch': '6px', padding: '10px 12px', fontSize: 13,
                      lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#1C3D2E',
                    }}>
                      {previewMessage(ev, { ...cfg.defaults.messages, ...messages },
                        { brand: brandName, reviewLinks }) || '— пусто —'}
                      {(ev === 'completed' || ev === 'review_invite') && hasReviewLinks && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                          {reviewButtonsFor(reviewLinks).map((b) => (
                            <span key={b.key} style={{
                              border: '1px solid #C1C8C4', background: '#fff', '--ch': '6px', '--stroke': '#C1C8C4',
                              padding: '5px 10px', fontSize: 12, color: '#1C3D2E',
                            }}>{b.title}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {(ev === 'completed' || ev === 'review_invite') && (
                      <small style={{ color: '#999', fontSize: 12 }}>
                        {hasReviewLinks
                          ? 'Ссылки на отзывы уходят кнопками под сообщением — в текст их вставлять не нужно.'
                          : 'Ссылки на отзывы не заданы (вкладка «Общие») — кнопок и приглашения к отзыву в письме не будет.'}
                      </small>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Сохранение…' : 'Сохранить тексты'}
        </button>
        <button className="btn btn-secondary btn-sm"
          onClick={() => setMessages({ ...cfg.defaults.messages })}
          disabled={areDefault}>
          Сбросить к стандартным
        </button>
      </div>
    </div>
  );
}

export default OrderStatusesSettings;
